import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { motion } from 'framer-motion';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 20 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const ProjectCard = ({ project, index }) => {
  return (
    <motion.div
      variants={fadeIn}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, delay: index * 0.1 }}
    >
      <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300 bg-white">
        <div className="relative h-56 overflow-hidden">
          <motion.img
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
        <CardContent className="p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="font-semibold text-lg mb-3 group-hover:text-blue-600 transition-colors duration-300">
              {project.title}
            </h3>
            <p className="text-sm text-gray-600 mb-4 line-clamp-3">
              {project.description}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm text-gray-500">{project.location}</span>
              </div>
              <Link 
                to="/contact"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center"
              >
                <motion.span
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Learn More →
                </motion.span>
              </Link>
            </div>
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ProjectsPage = () => {
  const projects = {
    industrial: [
      {
        title: "BD Associates Ltd - Tema Freezone",
        description: "Installation of 6000A ATS Power Panel for industrial processing facility. Our team implemented a state-of-the-art power distribution system ensuring reliable and efficient operations.",
        image: "/assets/bdasso.jpg",
        location: "Tema Freezone"
      },
      {
        title: "Supperlock Limited (STL)",
        description: "Installation of 250kVA AVR system for civil construction and printing operations. Comprehensive power management solution tailored for high-demand industrial applications.",
        image: "/assets/stl.jpg",
        location: "Greater Accra"
      },
      // {
      //   title: "Coco Processing Factory",
      //   description: "Complete electrical system installation and power distribution setup for BD Associate Ltd's processing facility. Integrated smart control systems for optimal energy efficiency.",
      //   image: "/api/placeholder/400/300",
      //   location: "Tema"
      // }
    ],
    commercial: [
      {
        title: "TDC Community 2 Market Building",
        description: "Comprehensive electrical installation including lighting systems and power distribution for the market complex. Enhanced safety features and energy-efficient lighting solutions.",
        image: "/assets/tdc.jpg",
        location: "Community 2, Tema"
      },
      {
        title: "FT Global Investment - UPSA Hostel",
        description: "10-story electrical installation project including conduit pipes and complete electrical setup for hostel Block C. Smart building integration with advanced safety systems.",
        image: "/assets/upsa.jpg",
        location: "UPSA Campus"
      },
      {
        title: "Ghana Stock Exchange",
        description: "Specialized electrical and data infrastructure installation for trading operations. High-reliability systems with redundant power supplies and UPS integration.",
        image: "/assets/stock.jpg",
        location: "Accra"
      }
    ],
    solar: [
      {
        title: "Solar Power Solutions",
        description: "Supply and installation of solar electricity power and generating systems for sustainable energy solutions. Custom-designed systems for optimal energy harvesting.",
        image: "/assets/solar.png",
        location: "Various Locations"
      },
      {
        title: "Backup Power Systems",
        description: "Integrated UPS, Generator, and Solar/Inverter installations for reliable power backup. Seamless power transition systems with smart grid integration.",
        image: "/assets/backup.jpg",
        location: "Greater Accra"
      }
    ],
    security: [
      {
        title: "Gateway Services Ltd Scanner Installation",
        description: "Installation of Cargo-fixed Scanner system with advanced detection capabilities for destination inspection. State-of-the-art security infrastructure.",
        image: "/assets/sentry.jpg",
        location: "Tema Harbour"
      },
      {
        title: "Nick TC Scan Ltd CCTV System",
        description: "Implementation of comprehensive CCTV surveillance system and X-Ray head power panel replacement. Advanced monitoring and security solutions.",
        image: "/assets/nicktc.jpg",
        location: "Tema Harbour"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800">
            Our Projects
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Delivering Results, Exceeding Expectations Since 1997
          </p>
        </motion.div>

        <Tabs defaultValue="industrial" className="w-full">
          <div className="bg-white rounded-lg shadow-md p-2 mb-12">
            <TabsList className="grid w-full grid-cols-4 gap-2">
              {Object.keys(projects).map((category) => (
                <TabsTrigger
                  key={category}
                  value={category}
                  className="capitalize text-sm font-medium py-3 hover:bg-blue-50 transition-colors duration-300"
                >
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {Object.entries(projects).map(([category, projectList]) => (
            <TabsContent key={category} value={category}>
              <motion.div
                variants={stagger}
                initial="initial"
                animate="animate"
                exit="exit"
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {projectList.map((project, index) => (
                  <ProjectCard key={index} project={project} index={index} />
                ))}
              </motion.div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default ProjectsPage;